import { PERMISSIONS } from "@/constants/permissions"
import useUser from "@/hooks/useUser"
import { useSearchParams } from "react-router"
import { CustomerActiveWorkSection } from "../../shared/CustomerActiveWorkSection"
import { EntityEmptyDetail } from "../../shared/EntityEmptyDetail"
import { GenerateConformityPanel } from "../../shared/GenerateConformityPanel"
import { CustomerAuditSection } from "./CustomerAuditSection"
import {
  CustomerFieldsBody,
  CustomerFieldsHeader,
  useCustomerFieldsState,
} from "./CustomerFieldsSection"
import { CustomerProductsBoughtSection } from "./CustomerProductsBoughtSection"

interface CustomerDetailPanelProps {
  selectedIds: Set<string>
  onRemoveIds: (ids: string[]) => void
}

export function CustomerDetailPanel({
  selectedIds,
  onRemoveIds,
}: CustomerDetailPanelProps) {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const fieldsState = useCustomerFieldsState()
  // ActiveWorkSection fetches questionnaires + conformity — 403s (and holds
  // its skeletons open forever) for members without either view permission.
  // Skip rendering entirely rather than showing a stuck loading state.
  const { hasPermission } = useUser()
  const canSeeActiveWork =
    hasPermission(PERMISSIONS.MC_QUESTIONNAIRES_VIEW) ||
    hasPermission(PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW)

  if (selectedIds.size > 0) {
    return (
      <GenerateConformityPanel
        entityType="customer"
        selectedIds={selectedIds}
        onRemoveIds={onRemoveIds}
      />
    )
  }

  if (!id) return <EntityEmptyDetail body="MC_MD_EMPTY_SELECT_BODY_CUSTOMER" />

  return (
    <div className="flex flex-col">
      <CustomerFieldsHeader state={fieldsState} />
      {canSeeActiveWork && (
        <CustomerActiveWorkSection scope="customer" entityId={id} />
      )}
      {/* The editable mc_customer_products mapping replaced the read-only
          relation view — customer-scope declarations resolve subjects from it. */}
      <CustomerProductsBoughtSection />
      <CustomerFieldsBody state={fieldsState} />
      <CustomerAuditSection />
    </div>
  )
}
